import { mapSeries } from "bluebird";
import { type AsyncReturnType } from "type-fest";
import { array, boolean, mixed, number, object } from "yup";
import { type AppContext } from "~/lib/context";
import { getRequiredUser } from "~/lib/getRequiredUser";
import { getDefaultCurrency } from "~/services/currency";
import { CUSTOM_JOB_PREFIX } from "~/services/custom-jobs/customJobPrefix";
import { EmployeeLevel } from "~/services/employee/employeeLevel";
import { getDefaultLocations } from "~/services/employee/employeeLocation";
import { fetchFiltersPresets } from "~/services/filters-preset/fetchFiltersPresets";
import { CUSTOM_JOBS_FAMILY_ID } from "~/services/job/fetchJobFamiliesForSelector";
import { DEFAULT_MARKET_DATA_MEASURE } from "~/services/market-data/market-data-filters/parseQueryString";
import { getDefaultLevels } from "~/services/market-data/market-data-filters/utils";
import { DefaultTargetPercentiles } from "~/services/percentiles";

const PresetFilterInputSchema = object({
  levels: array()
    .of(mixed<EmployeeLevel>().oneOf(Object.values(EmployeeLevel)).nullable())
    .nullable()
    .default([]),
  percentiles: object({
    low: number().min(0).max(100).required(),
    middle: number().min(0).max(100).required(),
    high: number().min(0).max(100).required(),
  }).nullable(),
  mergeAdvancedLevels: boolean().nullable(),
});

export const convertFiltersPresetsToInput = async (ctx: AppContext) => {
  const user = getRequiredUser(ctx);

  const defaultCurrency = await getDefaultCurrency(ctx);
  const defaultLocations = await getDefaultLocations(ctx);

  const presets = await fetchFiltersPresets(ctx);

  return mapSeries(presets, async (preset) => {
    const market = preset?.marketFilters;

    return {
      currency: defaultCurrency,
      measure: DEFAULT_MARKET_DATA_MEASURE,
      levels: getDefaultLevels(user),
      mergeAdvancedLevels: !user?.company.useAdvancedLevels,
      showCompanyEmployees: true,

      percentiles: DefaultTargetPercentiles,
      ...preset,
      market: {
        jobs: [
          ...preset.jobs.map((job) => ({ ...job, id: `${job.id}`, familyId: `${job.familyId}` })),
          ...preset.customJobs.map((job) => ({
            ...job,
            id: `${CUSTOM_JOB_PREFIX}${job.id}`,
            familyId: CUSTOM_JOBS_FAMILY_ID,
          })),
        ],
        fundingRounds: market.fundingRounds ?? [],
        headcount: market.headcount ?? null,
        industries: market.industries ?? [],
        industry: market.industry ?? null,
        labels: market.labels ?? [],
        locations: market.locations ?? defaultLocations,
        size: { min: market.minEmployeesCount, max: market.maxEmployeesCount },
      },
      input: PresetFilterInputSchema.validateSync(preset.input),
    };
  });
};

export type ConvertFiltersPresetsToInputResult = AsyncReturnType<typeof convertFiltersPresetsToInput>;
